/**
 * Iter-156A.5 Phase 5 — Archive read-only banner.
 *
 * Renders ONLY when the current project is archived. Always visible
 * to every role (Owner / Administrator / Contributor / Viewer) so
 * there's never any doubt about why write affordances are gone.
 *
 * - Owner sees an Unarchive button inline.
 * - Non-owners see calm read-only messaging.
 *
 * Source of truth is the same /permissions/me endpoint the rest of
 * the FE already calls — we additively read `archived` (Phase 5
 * adds this top-level mirror to the response).
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Archive, FolderOpen, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { lifecycleEndpoint } from "../lib/lifecycleEndpoint";
import { toast } from "sonner";
import { useApiBase } from "../context/ApiBaseContext";

export default function ArchiveBanner() {
    const { projectId } = useParams();
    const apiBase = useApiBase();
    const [archived, setArchived] = useState(false);
    const [isOwner, setIsOwner] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!projectId) {
            setArchived(false);
            setIsOwner(false);
            return;
        }
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get(
                    `${apiBase}/${projectId}/permissions/me`,
                );
                if (cancelled) return;
                setArchived(Boolean(data?.archived));
                setIsOwner(Boolean(data?.is_owner));
            } catch {
                if (!cancelled) {
                    setArchived(false);
                    setIsOwner(false);
                }
            }
        };
        load();
        // Refresh whenever the route changes (the user re-enters this
        // project from MyProjects). 60s background poll catches another
        // owner unarchiving from a parallel session.
        const t = setInterval(load, 60000);
        return () => {
            cancelled = true;
            clearInterval(t);
        };
    }, [projectId, apiBase]);

    if (!projectId || !archived) return null;

    const onUnarchive = async () => {
        setBusy(true);
        try {
            // Iter-158.13.2.17d — Unarchive is Production-scope
            // lifecycle served at /api/beta/projects/{pid}/unarchive
            // for Route 2 (NOT under the workspace prefix). Helper
            // resolves the correct endpoint so callers inside
            // /beta/hm-app/… stop 404'ing.
            await api.post(lifecycleEndpoint(apiBase, projectId, "unarchive"));
            setArchived(false);
            toast.success(
                "Project restored. Editing is enabled again — your roles and continuity are exactly as they were.",
            );
            // No router reload needed — the rest of the app polls
            // /permissions/me and will pick up the unlocked perms
            // within the existing fetch cycle. For an instant feel,
            // reload the current page so freshly-mounted components
            // see writable perms immediately.
            setTimeout(() => window.location.reload(), 400);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="archive-banner"
            role="status"
            className="border-b border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10"
        >
            <div className="max-w-screen-md mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <div className="flex items-start gap-2.5 flex-1 min-w-0">
                    <Archive
                        size={16}
                        className="text-amber-700 dark:text-amber-200 mt-0.5 shrink-0"
                    />
                    <div className="text-[13px] leading-relaxed text-amber-900 dark:text-amber-50">
                        <p
                            data-testid="archive-banner-title"
                            className="font-semibold"
                        >
                            This project is archived.
                        </p>
                        <p
                            data-testid="archive-banner-body"
                            className="opacity-95"
                        >
                            {isOwner
                                ? "Editing is disabled for the entire team while archived. Nothing has been lost — every photo, note and look is still here. Restore it any time."
                                : "You can still view continuity records, but editing is disabled until the project is restored. Nothing has been lost."}
                        </p>
                    </div>
                </div>
                {isOwner ? (
                    <button
                        type="button"
                        data-testid="archive-banner-unarchive"
                        onClick={onUnarchive}
                        disabled={busy}
                        className="tap inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-md text-xs font-semibold bg-amber-500 hover:bg-amber-400 text-neutral-900 disabled:opacity-50 shrink-0"
                    >
                        {busy ? (
                            <Loader2 size={12} className="animate-spin" />
                        ) : (
                            <FolderOpen size={12} />
                        )}
                        Unarchive project
                    </button>
                ) : null}
            </div>
        </div>
    );
}
